import { toSnakeCase } from '../packages/utils/casing'
import { EnrichedWarrantEntity, WarrantEntity, WarrantSubscriptionPeriod } from '../types/WarrantEntity'
import { findSubscriptionPeriodCloseToDate } from '../utils/warrants'
import ApiBase from './Api'

const WARRANT_API_PATHS = {
    WARRANTS: '/warrants',
    WARRANT: (id: string) => `/warrants/${id}`,
    ADMIN_WARRANTS: '/admin/warrants',
    ADMIN_WARRANT: (id: string) => `/admin/warrants/${id}`,
}

const sortSubscriptionPeriods = (
    a: Pick<WarrantSubscriptionPeriod, 'subStartDate'>,
    b: Pick<WarrantSubscriptionPeriod, 'subStartDate'>,
) =>
    a.subStartDate === null
        ? 1
        : b.subStartDate === null
          ? -1
          : new Date(a.subStartDate).getTime() - new Date(b.subStartDate).getTime()

class WarrantApi extends ApiBase {
    async findAll(): Promise<WarrantEntity[]> {
        const warrants = await this._getRequest(WARRANT_API_PATHS.WARRANTS)
        return warrants as WarrantEntity[]
    }

    async findByWarrantId(warrantId: string) {
        if (!warrantId) {
            return null
        }
        const warrant: WarrantEntity = await this._getRequest(WARRANT_API_PATHS.WARRANT(warrantId))

        const currentPeriod = findSubscriptionPeriodCloseToDate(warrant, new Date().toISOString())

        return {
            ...warrant,
            subscriptionPeriods: warrant.subscriptionPeriods.sort(sortSubscriptionPeriods),
            currentSubscriptionPeriod: currentPeriod,
        } as EnrichedWarrantEntity
    }

    async findByCompanyId(companyId: string): Promise<WarrantEntity[]> {
        if (!companyId) {
            return []
        }
        const warrants = await this._getRequest(WARRANT_API_PATHS.WARRANTS, {
            queryParameters: toSnakeCase({ companyId }),
        })
        return warrants as WarrantEntity[]
    }

    async findByIssueId(issueId: string): Promise<WarrantEntity[]> {
        if (!issueId) {
            return []
        }
        const warrants = await this._getRequest(WARRANT_API_PATHS.WARRANTS, {
            queryParameters: toSnakeCase({ issueId }),
        })
        return warrants as WarrantEntity[]
    }

    async create(data): Promise<WarrantEntity | null> {
        if (!data) {
            return null
        }
        const warrant = await this._postRequest(WARRANT_API_PATHS.WARRANTS, toSnakeCase(data))
        return warrant as WarrantEntity
    }

    async update(warrantId: string, data): Promise<WarrantEntity | null> {
        if (!data) {
            return null
        }
        const updatedWarrant = await this._putRequest(WARRANT_API_PATHS.ADMIN_WARRANT(warrantId), toSnakeCase(data))
        return updatedWarrant as WarrantEntity
    }

    /**
     * Changes specific fields of a warrant. The difference between this method and the update method is that this method
     * only updates the fields that are passed in the data object. The update method updates all fields.
     * Note that only certain fields are allowed to be updated with the PATCH method. Currently only the isinWarrant
     * field is allowed.
     */
    async patchFields(warrantId: string, data: Record<string, string | number>): Promise<WarrantEntity | null> {
        if (!data || Object.keys(data).length === 0) {
            return null
        }
        // whitelistedFields are the fields that are allowed to be updated with the PATCH method
        const whitelistedFields = ['isinWarrant']
        const fieldsToUpdate: Record<string, string | number> = {}
        whitelistedFields.forEach((field) => {
            if (data[field] !== undefined) {
                fieldsToUpdate[field] = data[field]
            }
        })
        if (Object.keys(fieldsToUpdate).length === 0) {
            console.error('No whitelisted fields to update. ', data)
            return null
        }
        const updatedWarrant = await this._patchRequest(
            WARRANT_API_PATHS.ADMIN_WARRANT(warrantId),
            toSnakeCase(fieldsToUpdate),
        )
        return updatedWarrant
    }

    async delete(warrantId: string): Promise<WarrantEntity | null> {
        if (!warrantId) {
            return null
        }
        const deletedWarrant: WarrantEntity = await this._deleteRequest(WARRANT_API_PATHS.ADMIN_WARRANT(warrantId))
        return deletedWarrant
    }
}

export default WarrantApi
